import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BookOpen, Calendar, FileText, Search, Loader2, Pencil, ClipboardList, Filter, Paperclip, Sparkles, Database, Play, BarChart3, Download, X } from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { ar } from "date-fns/locale";
import AddLessonDialog from "@/components/dialogs/AddLessonDialog";
import EditLessonDialog from "@/components/dialogs/EditLessonDialog";
import AddExamDialog from "@/components/dialogs/AddExamDialog";
import AddAssignmentDialog from "@/components/dialogs/AddAssignmentDialog";
import ExamResultsDialog from "@/components/dialogs/ExamResultsDialog";
import DeleteConfirmDialog from "@/components/dialogs/DeleteConfirmDialog";
import GenerateQuestionsDialog from "@/components/dialogs/GenerateQuestionsDialog";
import QuestionBankDialog from "@/components/dialogs/QuestionBankDialog";
import AutoExamDialog from "@/components/dialogs/AutoExamDialog";
import SmartExamWizard from "@/components/dialogs/SmartExamWizard";


const Lessons = () => {
  const { profile } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const nurseryId = profile?.nursery_id;
  const [search, setSearch] = useState("");
  const [classFilter, setClassFilter] = useState<string>("all");
  const [subjectFilter, setSubjectFilter] = useState<string>("all");
  const [resultsExam, setResultsExam] = useState<{ id: string; title: string; class_id: string | null } | null>(null);
  const [generateLesson, setGenerateLesson] = useState<{ id: string; title: string; content: string } | null>(null);
  const [showQuestionBank, setShowQuestionBank] = useState(false);
  const [editLesson, setEditLesson] = useState<{ id: string; title: string; content: string | null; class_id: string | null; scheduled_at: string | null; media_url?: string | null } | null>(null);
  const [fullscreenPdf, setFullscreenPdf] = useState<{ url: string; name: string; type?: string } | null>(null);
  const [showAutoExam, setShowAutoExam] = useState(false);

  // Close fullscreen on Escape key
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setFullscreenPdf(null);
    };
    if (fullscreenPdf) window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [fullscreenPdf]);
  const [assignmentFromLesson, setAssignmentFromLesson] = useState<{ lessonId: string; classId: string | null; title: string } | null>(null);

  const subjects = [
    { value: "all", label: "جميع المواد" },
    { value: "عربي", label: "اللغة العربية" },
    { value: "رياضيات", label: "الرياضيات" },
    { value: "انجليزي", label: "اللغة الإنجليزية" },
    { value: "علوم", label: "العلوم" },
    { value: "اسلامي", label: "التربية الإسلامية" },
  ];

  const matchesSubject = (title: string, subject: string) => {
    if (subject === "all") return true;
    const keywords: Record<string, string[]> = {
      "عربي": ["عربي", "حروف", "كتابة", "قراءة", "نحو", "إملاء", "خط", "أحرف", "حرف", "كلمات", "جمل", "نص", "فقرة", "تعبير"],
      "رياضيات": ["رياضيات", "أرقام", "جمع", "طرح", "ضرب", "قسمة", "عدد", "أعداد", "حساب", "هندس", "أشكال"],
      "انجليزي": ["english", "انجليزي", "إنجليزي", "letters", "alphabet", "الإنجليزية"],
      "علوم": ["علوم", "حيوان", "نبات", "كائن", "طبيعة", "جسم", "حواس", "ماء", "هواء", "طقس", "فصول"],
      "اسلامي": ["إسلام", "قرآن", "سورة", "صلاة", "صوم", "وضوء", "دعاء", "نبي", "رسول", "الله", "إيمان", "نوح", "محمد", "إبراهيم"],
    };
    const kws = keywords[subject] || [];
    const lower = title.toLowerCase();
    return kws.some((kw) => lower.includes(kw.toLowerCase()));
  };

  const { data: classes } = useQuery({
    queryKey: ["classes-list", nurseryId],
    queryFn: async () => {
      const { data } = await supabase.from("classes").select("id, name").eq("nursery_id", nurseryId!);
      return data || [];
    },
    enabled: !!nurseryId,
  });

  const { data: lessons, isLoading: lessonsLoading, refetch: refetchLessons } = useQuery({
    queryKey: ["lessons", nurseryId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("lessons")
        .select("*, classes(name)")
        .eq("nursery_id", nurseryId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: !!nurseryId,
  });

  const { data: exams, isLoading: examsLoading, refetch: refetchExams } = useQuery({
    queryKey: ["exams", nurseryId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("exams")
        .select("*, classes(name)")
        .eq("nursery_id", nurseryId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: !!nurseryId,
  });

  // Count results per exam
  const { data: resultCounts, refetch: refetchCounts } = useQuery({
    queryKey: ["exam-result-counts", nurseryId],
    queryFn: async () => {
      const examIds = (exams || []).map((e) => e.id);
      if (!examIds.length) return {};
      const { data } = await supabase.from("exam_results").select("exam_id").in("exam_id", examIds);
      const counts: Record<string, number> = {};
      (data || []).forEach((r) => { counts[r.exam_id] = (counts[r.exam_id] || 0) + 1; });
      return counts;
    },
    enabled: !!(exams && exams.length > 0),
  });


  // Count files per lesson
  const { data: lessonFileCounts } = useQuery({
    queryKey: ["lesson-file-counts", nurseryId],
    queryFn: async () => {
      const lessonIds = (lessons || []).map((l) => l.id);
      if (!lessonIds.length) return {};
      const { data } = await supabase.from("lesson_files").select("lesson_id").in("lesson_id", lessonIds);
      const counts: Record<string, number> = {};
      (data || []).forEach((r: any) => { counts[r.lesson_id] = (counts[r.lesson_id] || 0) + 1; });
      return counts;
    },
    enabled: !!(lessons && lessons.length > 0),
  });

  const filteredLessons = (lessons || []).filter((l) => {
    const matchSearch = l.title.includes(search) || (l.content && l.content.includes(search));
    const matchClass = classFilter === "all" || l.class_id === classFilter;
    const matchSubj = matchesSubject(l.title + " " + (l.content || ""), subjectFilter);
    return matchSearch && matchClass && matchSubj;
  });
  const filteredExams = (exams || []).filter((e) => {
    const matchSearch = e.title.includes(search);
    const matchClass = classFilter === "all" || e.class_id === classFilter;
    return matchSearch && matchClass;
  });

  const formatDate = (d: string | null) => {
    if (!d) return "-";
    try { return format(new Date(d), "d MMM yyyy", { locale: ar }); } catch { return d; }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">الدروس والامتحانات</h1>
          <p className="text-muted-foreground text-sm mt-1">إدارة المحتوى التعليمي والاختبارات</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => setShowQuestionBank(true)}>
            <Database className="w-4 h-4 ml-2" />
            📚 بنك الأسئلة
          </Button>
          <Button variant="outline" onClick={() => setShowAutoExam(true)}>
            <Sparkles className="w-4 h-4 ml-2" />
            📝 امتحان من البنك
          </Button>
          <SmartExamWizard classes={classes || []} onSuccess={refetchExams} />
          <AddLessonDialog classes={classes || []} onSuccess={refetchLessons} />
          <AddExamDialog classes={classes || []} onSuccess={refetchExams} />
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="بحث..." value={search} onChange={(e) => setSearch(e.target.value)} className="pr-9" />
        </div>
        <Select value={classFilter} onValueChange={setClassFilter}>
          <SelectTrigger className="w-full sm:w-[180px]">
            <Filter className="w-4 h-4 ml-2 text-muted-foreground" />
            <SelectValue placeholder="الفصل" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">جميع الفصول</SelectItem>
            {(classes || []).map((c) => (
              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={subjectFilter} onValueChange={setSubjectFilter}>
          <SelectTrigger className="w-full sm:w-[180px]">
            <BookOpen className="w-4 h-4 ml-2 text-muted-foreground" />
            <SelectValue placeholder="المادة" />
          </SelectTrigger>
          <SelectContent>
            {subjects.map((s) => (
              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Tabs defaultValue="lessons" dir="rtl">
        <TabsList>
          <TabsTrigger value="lessons">الدروس ({filteredLessons.length})</TabsTrigger>
          <TabsTrigger value="exams">الامتحانات ({filteredExams.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="lessons" className="mt-4 space-y-3">
          {lessonsLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
          ) : filteredLessons.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">لا توجد دروس</div>
          ) : (
            filteredLessons.map((l, i) => (
              <motion.div key={l.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
                <Card className="shadow-card cursor-pointer hover:shadow-elevated transition-shadow overflow-hidden" onClick={() => navigate(`/dashboard/lesson/${l.id}`)}>
                  <CardContent className="p-0">
                    <div className="flex">
                      {/* Cover Image */}
                      {l.media_url ? (
                        <div className="w-24 h-24 sm:w-32 sm:h-28 shrink-0">
                          <img src={l.media_url} alt={l.title} className="w-full h-full object-cover" />
                        </div>
                      ) : (
                        <div className="w-24 h-24 sm:w-32 sm:h-28 shrink-0 bg-muted flex items-center justify-center">
                          <BookOpen className="w-8 h-8 text-muted-foreground/40" />
                        </div>
                      )}
                      <div className="flex-1 p-4 flex items-center justify-between min-w-0">
                        <div className="min-w-0">
                          <p className="font-medium text-card-foreground">{l.title}</p>
                          <p className="text-xs text-muted-foreground">{(l as any).classes?.name || "بدون فصل"}</p>
                          {l.content && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{l.content}</p>}
                        </div>
                        <div className="flex items-center gap-3 shrink-0 mr-3" onClick={(e) => e.stopPropagation()}>
                      {lessonFileCounts?.[l.id] ? (
                        <Badge variant="secondary" className="text-xs flex items-center gap-1">
                          <Paperclip className="w-3 h-3" />
                          {lessonFileCounts[l.id]}
                        </Badge>
                      ) : null}
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Calendar className="w-3 h-3" />
                        {formatDate(l.scheduled_at)}
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => setEditLesson({
                          id: l.id,
                          title: l.title,
                          content: l.content,
                          class_id: l.class_id,
                          scheduled_at: l.scheduled_at,
                          media_url: l.media_url,
                        })}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <DeleteConfirmDialog
                        title="حذف الدرس"
                        description="هل أنت متأكد من حذف هذا الدرس؟"
                        onConfirm={async () => {
                          const { error } = await supabase.from("lessons").delete().eq("id", l.id);
                          if (error) toast({ title: "خطأ", description: error.message, variant: "destructive" });
                          else { toast({ title: "تم حذف الدرس" }); refetchLessons(); }
                        }}
                      />
                      </div>
                    </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))
          )}
        </TabsContent>

        <TabsContent value="exams" className="mt-4 space-y-3">
          {examsLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
          ) : filteredExams.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">لا توجد امتحانات</div>
          ) : (
            filteredExams.map((e, i) => (
              <motion.div key={e.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
                <Card className="shadow-card">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-secondary flex items-center justify-center shrink-0">
                        <FileText className="w-5 h-5 text-secondary-foreground" />
                      </div>
                      <div>
                        <p className="font-medium text-card-foreground">
                          {e.title}
                          {(e as any).is_interactive && <Badge variant="secondary" className="text-xs mr-2">تفاعلي</Badge>}
                        </p>
                        <p className="text-xs text-muted-foreground">{(e as any).classes?.name || "بدون فصل"}</p>
                        {(e as any).duration_minutes && <p className="text-xs text-muted-foreground">{(e as any).duration_minutes} دقيقة</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Calendar className="w-3 h-3" />
                        {formatDate(e.scheduled_at)}
                      </div>
                      {resultCounts?.[e.id] ? (
                        <Badge variant="outline" className="text-xs">{resultCounts[e.id]} نتيجة</Badge>
                      ) : null}
                      {(e as any).is_interactive && (
                        <>
                          <Button
                            variant="default"
                            size="sm"
                            onClick={() => navigate(`/dashboard/exam/${e.id}/take`)}
                          >
                            <Play className="w-3.5 h-3.5 ml-1" />
                            بدء الامتحان
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/dashboard/exam/${e.id}/results`)}
                          >
                            <BarChart3 className="w-3.5 h-3.5 ml-1" />
                            التقارير
                          </Button>
                        </>
                      )}
                      {!(e as any).is_interactive && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setResultsExam({ id: e.id, title: e.title, class_id: e.class_id })}
                        >
                          <ClipboardList className="w-3.5 h-3.5 ml-1" />
                          النتائج
                        </Button>
                      )}
                      <DeleteConfirmDialog
                        title="حذف الامتحان"
                        description="هل أنت متأكد من حذف هذا الامتحان؟ سيتم حذف جميع النتائج المرتبطة."
                        onConfirm={async () => {
                          await supabase.from("exam_results").delete().eq("exam_id", e.id);
                          await supabase.from("exam_answers").delete().match({});
                          await supabase.from("exam_submissions").delete().eq("exam_id", e.id);
                          await supabase.from("exam_questions").delete().eq("exam_id", e.id);
                          const { error } = await supabase.from("exams").delete().eq("id", e.id);
                          if (error) toast({ title: "خطأ", description: error.message, variant: "destructive" });
                          else { toast({ title: "تم حذف الامتحان" }); refetchExams(); }
                        }}
                      />
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))
          )}
        </TabsContent>
      </Tabs>

      {nurseryId && (
        <ExamResultsDialog
          open={!!resultsExam}
          onOpenChange={(o) => !o && setResultsExam(null)}
          examId={resultsExam?.id || null}
          examTitle={resultsExam?.title || ""}
          classId={resultsExam?.class_id || null}
          nurseryId={nurseryId}
          onSuccess={() => { refetchCounts(); }}
        />
      )}

      {/* Generate Questions Dialog */}
      {nurseryId && generateLesson && (
        <GenerateQuestionsDialog
          open={!!generateLesson}
          onOpenChange={(o) => !o && setGenerateLesson(null)}
          lessonId={generateLesson.id}
          lessonTitle={generateLesson.title}
          lessonContent={generateLesson.content}
          nurseryId={nurseryId}
          classId={(generateLesson as any).classId}
          onSuccess={() => { refetchExams(); }}
        />
      )}

      {/* Create Assignment from Lesson */}
      {nurseryId && assignmentFromLesson && (
        <AddAssignmentDialog
          classes={classes || []}
          nurseryId={nurseryId}
          onSuccess={() => { setAssignmentFromLesson(null); }}
          controlledOpen={!!assignmentFromLesson}
          onOpenChange={(o) => !o && setAssignmentFromLesson(null)}
          defaultLessonId={assignmentFromLesson.lessonId}
          defaultClassId={assignmentFromLesson.classId || undefined}
          defaultTitle={assignmentFromLesson.title}
        />
      )}

      {editLesson && (
        <EditLessonDialog
          lesson={editLesson}
          classes={classes || []}
          open={!!editLesson}
          onOpenChange={(o) => !o && setEditLesson(null)}
          onSuccess={refetchLessons}
        />
      )}

      {/* Question Bank Dialog */}
      {nurseryId && (
        <QuestionBankDialog
          open={showQuestionBank}
          onOpenChange={setShowQuestionBank}
          nurseryId={nurseryId}
          lessons={(lessons || []).map((l) => ({ id: l.id, title: l.title }))}
          onAutoExam={() => { setShowQuestionBank(false); setShowAutoExam(true); }}
        />
      )}
      {nurseryId && (
        <AutoExamDialog
          open={showAutoExam}
          onOpenChange={setShowAutoExam}
          nurseryId={nurseryId}
          classes={classes || []}
          onSuccess={() => { refetchExams(); refetchCounts(); }}
        />
      )}

      {/* Fullscreen PDF Overlay */}
      {fullscreenPdf && (
        <div className="fixed inset-0 z-50 bg-black/80 flex flex-col" onClick={() => setFullscreenPdf(null)}>
          <div className="flex items-center justify-between p-3 bg-background/90 backdrop-blur-sm border-b" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 text-sm font-medium text-foreground">
              <FileText className="w-4 h-4" />
              {fullscreenPdf.name}
            </div>
            <div className="flex items-center gap-2">
              <a href={fullscreenPdf.url} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="sm">
                  <Download className="w-3.5 h-3.5 ml-1" />
                  تحميل
                </Button>
              </a>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setFullscreenPdf(null)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>
          <iframe
            src={fullscreenPdf.url}
            className="flex-1 w-full"
            title={fullscreenPdf.name}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default Lessons;
